import { useRef, useState, useCallback, type DragEvent } from 'react'
import { Upload, Loader2, X } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useImageUpload } from '@/hooks/use-image-upload'
import type { CustomInputDefinition } from '@/types/database'

interface CustomInputFieldsProps {
  definitions: CustomInputDefinition[]
  values: Record<string, string>
  onChange: (values: Record<string, string>) => void
  className?: string
}

interface UploadFieldState {
  status: 'idle' | 'uploading' | 'error'
  fileName?: string
  error?: string
}

function FileUploadField({
  def,
  value,
  onValueChange,
}: {
  def: CustomInputDefinition
  value: string
  onValueChange: (url: string) => void
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  const [state, setState] = useState<UploadFieldState>({ status: 'idle' })
  const [isDragging, setIsDragging] = useState(false)
  const isImage = def.type === 'image_upload'

  const { uploadImage } = useImageUpload({
    folder: 'custom-inputs',
    allowedTypes: isImage ? ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] : undefined,
  })

  const handleFile = useCallback(async (file: File) => {
    setState({ status: 'uploading', fileName: file.name })
    const result = await uploadImage(file)
    if (result && result.status === 'complete' && result.url) {
      onValueChange(`${result.url}||${file.name}`)
      setState({ status: 'idle', fileName: file.name })
    } else {
      setState({
        status: 'error',
        fileName: file.name,
        error: result?.error || 'Upload failed',
      })
    }
  }, [uploadImage, onValueChange])

  const handleRemove = () => {
    onValueChange('')
    setState({ status: 'idle' })
    if (inputRef.current) inputRef.current.value = ''
  }

  const handleDragOver = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDrop = (e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
    const file = e.dataTransfer.files?.[0]
    if (file) handleFile(file)
  }

  const hasValue = value?.startsWith('http')
  const uploadUrl = hasValue ? value.split('||')[0] : ''
  const storedFileName = hasValue ? (value.split('||')[1] || state.fileName || 'Uploaded file') : ''

  // Uploading state
  if (state.status === 'uploading') {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3">
        <Loader2 className="size-5 animate-spin text-zinc-400" />
        <span className="truncate text-sm text-zinc-600">{state.fileName}</span>
      </div>
    )
  }

  // Complete state (value is a URL)
  if (hasValue) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2">
        {isImage && (
          <img
            src={uploadUrl}
            alt="Upload preview"
            className="h-[60px] w-[60px] flex-shrink-0 rounded object-cover"
          />
        )}
        <span className="min-w-0 flex-1 truncate text-sm text-zinc-600">
          {storedFileName}
        </span>
        <button
          type="button"
          onClick={handleRemove}
          className="flex-shrink-0 rounded p-1 text-zinc-400 transition-colors hover:bg-zinc-200 hover:text-zinc-700"
          aria-label="Remove file"
        >
          <X className="size-4" />
        </button>
      </div>
    )
  }

  // Error state
  if (state.status === 'error') {
    return (
      <div className="space-y-2">
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
          <p className="text-sm text-red-600">{state.error}</p>
        </div>
        <button
          type="button"
          onClick={() => {
            setState({ status: 'idle' })
            inputRef.current?.click()
          }}
          className="text-sm font-medium text-red-600 hover:underline"
        >
          Try again
        </button>
        <input
          ref={inputRef}
          type="file"
          className="hidden"
          accept={isImage ? 'image/*' : undefined}
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) handleFile(file)
          }}
        />
      </div>
    )
  }

  // Idle state — styled upload zone with drag-and-drop
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click() }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={cn(
        'flex w-full cursor-pointer items-center gap-3 rounded-lg border-2 border-dashed px-4 py-4 text-left transition-colors',
        isDragging
          ? 'border-red-400 bg-red-50'
          : 'border-zinc-300 bg-zinc-50 hover:border-zinc-400 hover:bg-zinc-100'
      )}
    >
      <Upload className={cn('size-5 flex-shrink-0', isDragging ? 'text-red-500' : 'text-zinc-400')} />
      <span className={cn('text-sm', isDragging ? 'text-red-600' : 'text-zinc-500')}>
        {isDragging ? 'Drop file here' : `Drag & drop or click to upload ${isImage ? 'an image' : 'a file'}`}
      </span>
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept={isImage ? 'image/*' : undefined}
        onChange={e => {
          const file = e.target.files?.[0]
          if (file) handleFile(file)
        }}
      />
    </div>
  )
}

export function CustomInputFields({
  definitions,
  values,
  onChange,
  className,
}: CustomInputFieldsProps) {
  if (definitions.length === 0) return null

  const handleChange = (fieldId: string, value: string) => {
    onChange({ ...values, [fieldId]: value })
  }

  return (
    <div className={cn('space-y-4', className)}>
      {definitions
        .sort((a, b) => a.position - b.position)
        .map(def => (
          <div key={def.id}>
            <Label htmlFor={`custom-${def.id}`} className="mb-1.5 block text-sm font-medium text-zinc-700">
              {def.label}
              {def.required && <span className="ml-1 text-red-500">*</span>}
            </Label>

            {def.type === 'text' && (
              <Input
                id={`custom-${def.id}`}
                value={values[def.id] || ''}
                onChange={e => handleChange(def.id, e.target.value)}
                placeholder={def.placeholder}
                maxLength={def.max_length}
                required={def.required}
              />
            )}

            {def.type === 'textarea' && (
              <Textarea
                id={`custom-${def.id}`}
                value={values[def.id] || ''}
                onChange={e => handleChange(def.id, e.target.value)}
                placeholder={def.placeholder}
                maxLength={def.max_length}
                required={def.required}
                rows={3}
              />
            )}

            {(def.type === 'image_upload' || def.type === 'file_upload') && (
              <FileUploadField
                def={def}
                value={values[def.id] || ''}
                onValueChange={url => handleChange(def.id, url)}
              />
            )}

            {def.help_text && (
              <p className="mt-1 text-xs text-zinc-500">{def.help_text}</p>
            )}

            {def.max_length && values[def.id] && (
              <p className="mt-1 text-xs text-zinc-400">
                {values[def.id].length}/{def.max_length}
              </p>
            )}
          </div>
        ))}
    </div>
  )
}

/**
 * Check if all required custom inputs have been filled
 */
export function areCustomInputsComplete(
  definitions: CustomInputDefinition[],
  values: Record<string, string>
): boolean {
  return definitions
    .filter(d => d.required)
    .every(d => values[d.id]?.trim())
}
